"use client";

import { Calendar, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  PostsProvider,
  usePosts,
} from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted/30" />
        <div className="h-6 w-1/2 animate-pulse rounded-md bg-muted/30" />
        <div className="aspect-[16/9] w-full animate-pulse rounded-lg bg-muted/30" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded-md bg-muted/30" />
          <div className="h-4 w-full animate-pulse rounded-md bg-muted/30" />
          <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-muted-foreground">Failed to load post.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitials = post.author?.name
    ? post.author.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "LC";

  const authorAvatar =
    post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";

  return (
    <div className="mx-auto max-w-4xl">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center rounded-md border border-border bg-muted px-3 py-1 text-xs font-medium uppercase tracking-wider text-secondary"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl"
        style={{ fontFamily: "var(--font-serif)" }}
      >
        {post.title}
      </h1>

      {}
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        {post.author?.name && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.date}>{formattedDate}</time>
          </div>
        )}
      </div>

      <Separator className="my-8 bg-border" />

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      {post.content && (
        <article
          className="prose max-w-none font-default text-base leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-muted [&_blockquote]:px-6 [&_blockquote]:py-4 [&_blockquote]:italic [&_blockquote]:text-foreground/80 [&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-foreground [&_h4]:mb-3 [&_h4]:mt-6 [&_h4]:text-xl [&_h4]:font-semibold [&_h4]:text-foreground [&_img]:my-8 [&_img]:rounded-lg [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-6 [&_strong]:text-foreground [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Tags:
          </span>
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="inline-flex items-center rounded-md bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author?.name && (
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="flex flex-col items-center gap-6 p-8 text-center sm:flex-row sm:text-left">
            <Avatar className="h-20 w-20 rounded-lg">
              {authorAvatar && (
                <AvatarImage src={authorAvatar} alt={post.author.name} />
              )}
              <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-lg font-semibold">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Author
              </p>
              <h3
                className="mt-1 font-default text-2xl font-semibold text-foreground"
                style={{ fontFamily: "var(--font-serif)" }}
              >
                {post.author.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Tax professional at Lovejoy Company, dedicated to providing strategic financial guidance and compliance excellence.
              </p>
              {post.author.link && (
                <Link to={post.author.link} className="mt-4 inline-block">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-border bg-transparent text-foreground hover:bg-muted"
                  >
                    View all articles
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div
            data-index={i}
            key={i}
            className="space-y-4"
          >
            <div className="aspect-[4/3] w-full animate-pulse rounded-lg bg-muted/30" />
            <div className="h-5 w-3/4 animate-pulse rounded-md bg-muted/30" />
            <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted/30" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        No related tax insights available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "";

        return (
          <article
            data-index={i}
            key={post.id}
            className="group flex flex-col"
          >
            <Link to={post.link || "#"} className="block overflow-hidden rounded-lg">
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="aspect-[4/3] w-full bg-muted" />
              )}
            </Link>
            <div className="mt-5 flex flex-col">
              {post.categories && post.categories.length > 0 && (
                <span className="text-xs font-medium uppercase tracking-wider text-secondary">
                  {post.categories[0].name}
                </span>
              )}
              <h3
                className="mt-2 font-default text-xl font-semibold leading-snug text-foreground transition-colors group-hover:text-primary"
                style={{ fontFamily: "var(--font-serif)" }}
              >
                <Link to={post.link || "#"}>{post.title}</Link>
              </h3>
              {formattedDate && (
                <time
                  dateTime={post.date}
                  className="mt-3 text-xs text-muted-foreground"
                >
                  {formattedDate}
                </time>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="5033"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 px-4 sm:px-6 lg:py-28"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>

        <div className="mx-auto mt-24 max-w-6xl">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-secondary">
                Explore More
              </span>
              <h2
                className="mt-2 font-default text-3xl font-semibold tracking-tight text-foreground md:text-4xl"
                style={{ fontFamily: "var(--font-serif)" }}
              >
                Related Tax Insights
              </h2>
            </div>
            <Link to="#">
              <Button
                variant="outline"
                className="border-border bg-transparent text-foreground hover:bg-muted"
              >
                View All Resources
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}